'use client';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

interface Category {
    id: number;
    name: string;
    slug: string;
}

interface Props {
    categories: Category[];
    merchantName: string;
    slug: string;
}

const MerchantCategorySlider: React.FC<Props> = ({ categories, merchantName, slug }) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);

    const syncState = useCallback(() => {
        const t = trackRef.current;
        if (!t) return;
        setCanPrev(t.scrollLeft > 4);
        setCanNext(t.scrollLeft < t.scrollWidth - t.clientWidth - 4);
    }, []);

    useEffect(() => {
        const t = trackRef.current;
        if (!t) return;
        syncState();
        t.addEventListener('scroll', syncState, { passive: true });
        window.addEventListener('resize', syncState);
        return () => {
            t.removeEventListener('scroll', syncState);
            window.removeEventListener('resize', syncState);
        };
    }, [categories, syncState]);

    const scroll = (dir: 'left' | 'right') => {
        trackRef.current?.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
    };

    if (!categories?.length) return null;

    return (
        <div className="w-full bg-white border-b border-gray-100">
            <div className="container mx-auto px-4 md:px-8 max-w-7xl py-4">

                {/* Label row */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className="w-1 h-4 rounded-full bg-[#5D27A1]" />
                        <span className="text-xs font-semibold text-gray-500">
                            Shop by category · <span className="text-gray-800">{merchantName}</span>
                        </span>
                    </div>
                </div>

                {/* Slider row */}
                <div className="relative flex items-center gap-2">

                    {/* Prev button */}
                    <button
                        onClick={() => scroll('left')}
                        disabled={!canPrev}
                        aria-label="Scroll left"
                        className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 hover:border-[#5D27A1]/40 hover:text-[#5D27A1] disabled:opacity-0 disabled:pointer-events-none transition-all duration-200"
                    >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Track */}
                    <div
                        ref={trackRef}
                        className="flex gap-2 overflow-x-auto flex-1"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollSnapType: 'x mandatory' }}
                    >
                        {categories.map((item, i) => (
                            <Link
                                key={item.id || i}
                                href={`/products/${slug}/${item.slug}`}
                                className="flex-shrink-0 px-4 py-2 rounded-full text-xs font-medium text-gray-600 bg-gray-50 border border-gray-100 hover:bg-[#5D27A1]/8 hover:text-[#5D27A1] hover:border-[#5D27A1]/20 no-underline transition-all duration-200 whitespace-nowrap"
                                style={{ scrollSnapAlign: 'start' }}
                            >
                                {item.name}
                            </Link>
                        ))}
                    </div>

                    {/* Next button */}
                    <button
                        onClick={() => scroll('right')}
                        disabled={!canNext}
                        aria-label="Scroll right"
                        className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 hover:border-[#5D27A1]/40 hover:text-[#5D27A1] disabled:opacity-0 disabled:pointer-events-none transition-all duration-200"
                    >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MerchantCategorySlider;
